import { Input } from "@9amhealth/shared";
import type { FC } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { kgToPounds, poundsToKg } from "src/lib/unitConversion";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";

const WeightInput: FC<{
  field: QuestionnaireField;
}> = (props) => {
  const name = props.field.id;
  const { getValues, setValue } = useFormContext();
  const kg = Number(getValues(name) ?? 0);
  const [, { saveValue }] = useQuestionnaireCubitInContext();
  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);
  const [error, setError] = useState<string | undefined>(undefined);

  const inPounds = kgToPounds(kg);
  const pounds = useMemo(() => Math.round(inPounds), [inPounds]);

  const handleWeightChange = (newWeightString: string) => {
    setError(undefined);
    clearFieldError(name);
    let asNumber = 0;
    try {
      asNumber = Number.parseFloat(newWeightString);
    } catch (e) {
      reportErrorSentry(
        new Error(`WeightInput: handleWeightChange: parseFloat error`),
        {
          extra: {
            newWeightString
          },
          cause: e as Error
        }
      );
    }

    if (typeof asNumber !== "number" || isNaN(asNumber) || asNumber <= 0) {
      saveValue(name, undefined);
      setValue(name, undefined);
      clearFieldError(name);
      return;
    }

    const convertedToKg = poundsToKg(asNumber);
    if (isNaN(convertedToKg)) return;

    if (convertedToKg < 2 || convertedToKg > 300) {
      const errorMsg = translate("error_validate_weight_invalid");
      setError(errorMsg);
      setFieldError(name, errorMsg);
      return;
    }

    saveValue(name, convertedToKg);
    setValue(name, convertedToKg);
  };

  useEffect(() => {
    handleWeightChange(pounds.toString());
  }, [pounds]);

  return (
    <div>
      <Input
        name="weight"
        defaultValue={pounds ? `${pounds}` : ""}
        placeholder={translate("pounds")}
        label={translate("pounds")}
        isRequired
        maxLength={3}
        minLength={1}
        pattern="[0-9]*"
        mask="0[0][0]"
        inputMode="numeric"
        aria-label={translate("pounds")}
        icon={translate("units.pounds")}
        onChange={(e) => handleWeightChange(e)}
        error={error}
      />
    </div>
  );
};

export default WeightInput;
